import { api } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { formatLds, formatNumber, formatUsd } from '../../lib/format';
import { Card, Chip, ProgressBar, Spinner, StatCard, StatCards } from '../../ui';
import { INCOME_TABS } from './incomeTabs';

/**
 * Achievement Rewards are the only BINARY-tree income in the plan: a milestone
 * unlocks when BOTH the left and right legs reach it, so progress is gated by
 * the weaker leg. Every other income type runs on the unilevel sponsor tree.
 */
export function AchievementRewards() {
  const legs = useQuery({
    queryKey: ['binary-legs'],
    queryFn: () => api.team.binaryLegCounts().then((r) => r.data),
  });

  const rewards = useQuery({
    queryKey: ['achievement-rewards'],
    queryFn: () => api.income.achievementRewards().then((r) => r.data),
  });

  return (
    <>
      <PageHeader
        title="Achievement Rewards"
        description="Unlocked when both your Left and Right teams reach the same milestone."
        tabs={INCOME_TABS}
      />

      <StatCards>
        <StatCard
          label="Left Team"
          value={formatNumber(legs.data?.left_total)}
          sub={`${formatNumber(legs.data?.left_active)} active`}
        />
        <StatCard
          label="Right Team"
          value={formatNumber(legs.data?.right_total)}
          sub={`${formatNumber(legs.data?.right_active)} active`}
        />
        <StatCard label="Matched" value={formatNumber(legs.data?.matched)} />
      </StatCards>

      <Card>
        <h3 className="card-title">Milestones</h3>
        {rewards.isLoading ? (
          <Spinner />
        ) : (
          <ul className="tier-list">
            {rewards.data?.map((reward) => {
              // The matched pair is what pays, so progress is min(left, right).
              const matched = Math.min(reward.current_left, reward.current_right);
              return (
                <li key={reward.id} className="tier-row">
                  <div className="tier-head">
                    <strong>
                      {formatNumber(reward.required_left)} / {formatNumber(reward.required_right)}
                    </strong>
                    {reward.achieved ? (
                      <Chip tone="success">Achieved</Chip>
                    ) : (
                      <Chip tone="gold">Locked</Chip>
                    )}
                    <span className="text-gold">
                      {formatUsd(reward.reward_usd)}{' '}
                      <span className="text-muted small">({formatLds(reward.reward_lds)})</span>
                    </span>
                  </div>
                  <p className="text-muted small">
                    Left {formatNumber(reward.current_left)} · Right {formatNumber(reward.current_right)}
                  </p>
                  <ProgressBar
                    value={matched}
                    max={reward.milestone}
                    label={`${formatNumber(matched)} / ${formatNumber(reward.milestone)} matched`}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </>
  );
}
